import React, { useState } from "react";
import {
  Button,
  Box,
  Typography,
  makeStyles,
  Link,
  IconButton,
  Modal,
  Portal,
  useMediaQuery,
  Grid
} from "@material-ui/core";
import clsx from "clsx";
import ReactPlayer from "react-player";
import CancelIcon from "@material-ui/icons/Cancel";

import { ReactComponent as EduVideoIcon } from "../../assets/images/icons/edu-video.svg";
import { ReactComponent as EduLearnMoreIcon } from "../../assets/images/icons/edu-learn-more.svg";
import { useFirebaseRemoteConfig } from "../../states/firebase";
import mixpanelWrapper from "../../utils/mixpanel";
import { capitalize } from "../../utils/helpers";

const useStyles = makeStyles(theme => ({
  button: {
    display: "flex !important",
    textDecoration: "none !important",
    alignItems: "center",
    color: theme.palette.primary.main,
    fontWeight: 400,
    fontSize: theme.typography.pxToRem(15),
    "& .MuiButton-startIcon": {
      marginTop: -2,
      marginRight: 8
    }
  },
  educationalMaterialVideoModal: {
    height: "100%",
    width: "100%",
    display: "flex",
    flexWrap: "wrap",
    flexDirection: "column",
    justifyContent: "center",
    alignContent: "center"
  },
  educationalMaterialVideoContainer: {
    display: "flex",
    justifyContent: "center",
    alignItems: "center"
  },
  educationalMaterialVideoContainerLargeScreenSize: {
    width: "1280px",
    height: "720px"
  },
  educationalMaterialVideoContainerMediumScreenSize: {
    width: "640px",
    height: "480px"
  },
  educationalMaterialVideoContainerSmallScreenSize: {
    width: "480px",
    height: "320px"
  },
  educationalMaterialVideoCloseContainer: {
    display: "flex",
    justifyContent: "flex-start",
    alignItems: "center"
  },
  educationalMaterialVideoCloseText: {
    color: "#fff"
  },
  educationalMaterialVideoCloseIcon: {
    color: "#fff"
  }
}));

const EducationalMaterials = props => {
  const { configId, sectionName, showArticle, articleButtonText, showVideo } =
    props;

  // General
  const classes = useStyles();
  const isMediumSize = useMediaQuery(
    "(min-width: 800px) and (max-width: 1450px )"
  );
  const isLargeSize = useMediaQuery("(min-width: 1450px");

  // States
  const [isVideoOpen, setIsVideoOpen] = useState(false);

  // Customs
  const firebaseRemoteConfig = useFirebaseRemoteConfig();

  // Callbacks
  const handleVideoOpen = () => setIsVideoOpen(true);
  const handleVideoClose = () => setIsVideoOpen(false);

  const {
    educationalMaterialVideoModal,
    educationalMaterialVideoContainer,
    educationalMaterialVideoContainerLargeScreenSize,
    educationalMaterialVideoContainerMediumScreenSize,
    educationalMaterialVideoContainerSmallScreenSize,
    educationalMaterialVideoCloseContainer,
    educationalMaterialVideoCloseIcon,
    educationalMaterialVideoCloseText
  } = useStyles();

  const educationalMaterialVideoContainerSize = isLargeSize
    ? educationalMaterialVideoContainerLargeScreenSize
    : isMediumSize
    ? educationalMaterialVideoContainerMediumScreenSize
    : educationalMaterialVideoContainerSmallScreenSize;

  if (!firebaseRemoteConfig.ready) {
    return null;
  }

  return (
    <>
      <Grid container>
        <Grid item>
          {showArticle && (
            <Link
              component={Button}
              startIcon={<EduLearnMoreIcon />}
              href={firebaseRemoteConfig.getString(
                `edu_${configId}_article_url`
              )}
              target="_blank"
              className={classes.button}
              onClick={() =>
                mixpanelWrapper.track("Learn how it works button clicked", {
                  Section: capitalize(sectionName)
                })
              }
            >
              {articleButtonText || `Learn how ${sectionName} work`}
            </Link>
          )}
        </Grid>
        <Grid item>
          {showVideo && (
            <Button
              className={classes.button}
              onClick={() => {
                mixpanelWrapper.track("Watch a video button clicked", {
                  Section: capitalize(sectionName)
                });
                handleVideoOpen();
              }}
              startIcon={<EduVideoIcon />}
            >
              Watch a video
            </Button>
          )}
        </Grid>
      </Grid>
      <Portal>
        <Modal
          open={isVideoOpen}
          onClose={handleVideoClose}
          style={{ zIndex: 1355 }}
        >
          <Box className={educationalMaterialVideoModal}>
            <Box className={educationalMaterialVideoCloseContainer}>
              <IconButton onClick={handleVideoClose}>
                <CancelIcon className={educationalMaterialVideoCloseIcon} />
              </IconButton>
              <Typography className={educationalMaterialVideoCloseText}>
                Close video
              </Typography>
            </Box>
            <Box
              className={clsx(
                educationalMaterialVideoContainer,
                educationalMaterialVideoContainerSize
              )}
            >
              <ReactPlayer
                url={firebaseRemoteConfig.getString(
                  `edu_${configId}_video_url`
                )}
                controls
                light
                width="100%"
                height="100%"
              />
            </Box>
          </Box>
        </Modal>
      </Portal>
    </>
  );
};

export default EducationalMaterials;
